import React, { useEffect, useState, useCallback, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { getErrorMessage } from '../types';
import { useTranslation } from '../i18n';
import { formatDateOnly } from '../formatDate';
import { DateOfBirthInput } from '../components/DateOfBirthInput';
import { FormLabel } from '../components/FormLabel';
import { FormLegend } from '../components/FormLegend';
import { FieldError } from '../components/FieldError';
import { validateFormSubmit } from '../utils/formValidation';
import { isValidPhoneNumberInput, normalizePhoneNumber } from '../utils/phoneNumber';
import { PhoneInput } from '../components/PhoneInput';
import { useApiRequest } from '../useApiRequest';
import { translateOfferIssues } from '../offerEligibility';
import { COUNTRY_CODES, DEFAULT_COUNTRY, countryLabelKey } from '../utils/countries';
import { Modal } from '../components/Modal';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string | null;
  isVerified: boolean;
  verifiedAt?: string | null;
  verificationMethod?: string | null;
  verifiedByUsername?: string | null;
  hasExternalId: boolean;
  externalIdProvider?: string | null;
  externalIdSub?: string | null;
  isAdmin: boolean;
  phoneNumber?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  city?: string | null;
  country?: string | null;
  preciseLatitude?: number | null;
  preciseLongitude?: number | null;
  approximateLatitude?: number | null;
  approximateLongitude?: number | null;
  experiencePoints: number;
  level: number;
  listingCount: number;
  canOffer: boolean;
  offerIssues: string[];
}

interface WhitelistEntry {
  id: number;
  email: string;
  createdAt: string;
  autoVerify: boolean;
}

interface AdminUserDraft {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phoneNumber: string;
  street: string;
  houseNumber: string;
  city: string;
  country: string;
  isVerified: boolean;
  isAdmin: boolean;
}

function displayValue(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function userToDraft(user: AdminUser): AdminUserDraft {
  return {
    username: user.username,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    dateOfBirth: user.dateOfBirth ?? '',
    phoneNumber: user.phoneNumber ?? '',
    street: user.street ?? '',
    houseNumber: user.houseNumber ?? '',
    city: user.city ?? '',
    country: DEFAULT_COUNTRY,
    isVerified: user.isVerified,
    isAdmin: user.isAdmin,
  };
}

function draftToPayload(draft: AdminUserDraft) {
  return {
    username: draft.username,
    email: draft.email,
    firstName: draft.firstName,
    lastName: draft.lastName,
    dateOfBirth: draft.dateOfBirth || null,
    phoneNumber: normalizePhoneNumber(draft.phoneNumber),
    street: draft.street || null,
    houseNumber: draft.houseNumber || null,
    city: draft.city || null,
    country: draft.country || null,
    isVerified: draft.isVerified,
    isAdmin: draft.isAdmin,
  };
}

interface ReadOnlyFieldProps {
  label: string;
  value: React.ReactNode;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => (
  <div className="admin-user-readonly-field">
    <span className="admin-user-readonly-label">{label}</span>
    <span className="admin-user-readonly-value">{value}</span>
  </div>
);

interface UsersPageProps {
  currentUserId: number;
}

export const UsersPage: React.FC<UsersPageProps> = ({ currentUserId }) => {
  const { t, lang } = useTranslation();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<AdminUserDraft | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFieldError, setPasswordFieldError] = useState('');
  const [passwordUserId, setPasswordUserId] = useState<number | null>(null);
  const [xpAdjustAmount, setXpAdjustAmount] = useState('');
  const [adjustingXpId, setAdjustingXpId] = useState<number | null>(null);
  const [whitelist, setWhitelist] = useState<WhitelistEntry[]>([]);
  const [whitelistEmail, setWhitelistEmail] = useState('');
  const [whitelistAutoVerify, setWhitelistAutoVerify] = useState(true);
  const [whitelistLoading, setWhitelistLoading] = useState(true);
  const [whitelistSaving, setWhitelistSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'whitelist' | 'registered'>('registered');
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadWhitelist = useCallback(async () => {
    setWhitelistLoading(true);
    try {
      const res = await api.get<WhitelistEntry[]>('/users/admin/whitelist');
      setWhitelist(res.data);
    } catch (err) {
      setError(getErrorMessage(err, t('users.whitelist_load_error')));
    } finally {
      setWhitelistLoading(false);
    }
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get<AdminUser[]>('/users/admin');
      setUsers(res.data);
    } catch (err) {
      setError(getErrorMessage(err, t('users.load_error')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const { loading: saving, call: saveUser } = useApiRequest(
    async (userId: number, payload: ReturnType<typeof draftToPayload>) => {
      const res = await api.put<AdminUser>(`/users/admin/${userId}`, payload);
      setUsers(prev => prev.map(u => (u.id === userId ? res.data : u)));
    }
  );

  const { loading: settingPassword, call: setPassword } = useApiRequest(
    async (userId: number, password: string) => {
      await api.put(`/auth/change-password/${userId}`, { newPassword: password });
    }
  );

  useEffect(() => {
    load();
    loadWhitelist();
  }, [load, loadWhitelist]);

  const handleAddWhitelist = async (e: FormEvent) => {
    if (!validateFormSubmit(e)) return;
    e.preventDefault();
    const email = whitelistEmail.trim();
    if (!email) return;

    setWhitelistSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post<WhitelistEntry>('/users/admin/whitelist', { email, autoVerify: whitelistAutoVerify });
      setWhitelist(prev => [res.data, ...prev]);
      setWhitelistEmail('');
      setWhitelistAutoVerify(false);
      setSuccess(t('users.whitelist_add_success'));
    } catch (err) {
      setError(getErrorMessage(err, t('users.whitelist_add_error')));
    } finally {
      setWhitelistSaving(false);
    }
  };

  const handleRemoveWhitelist = async (id: number) => {
    setError('');
    setSuccess('');
    try {
      await api.delete(`/users/admin/whitelist/${id}`);
      setWhitelist(prev => prev.filter(entry => entry.id !== id));
      setSuccess(t('users.whitelist_remove_success'));
    } catch (err) {
      setError(getErrorMessage(err, t('users.whitelist_remove_error')));
    }
  };

  const toggleExpanded = (user: AdminUser) => {
    if (expandedId === user.id) {
      setExpandedId(null);
      setDraft(null);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordUserId(null);
      setXpAdjustAmount('');
      return;
    }
    setExpandedId(user.id);
    setDraft(userToDraft(user));
    setNewPassword('');
    setConfirmPassword('');
    setPasswordUserId(user.id);
    setXpAdjustAmount('');
    setSuccess('');
    setError('');
  };

  const handleCancel = () => {
    setExpandedId(null);
    setDraft(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordUserId(null);
    setPasswordUserId(null);
    setXpAdjustAmount('');
  };

  const handleAdjustXp = async (e: FormEvent, userId: number) => {
    e.preventDefault();
    const amount = parseInt(xpAdjustAmount, 10);
    if (Number.isNaN(amount) || amount === 0) {
      setError(t('users.xp_adjust_invalid'));
      return;
    }

    setAdjustingXpId(userId);
    setError('');
    setSuccess('');
    try {
      const res = await api.post<{ experiencePoints: number; level: number }>(
        `/users/admin/${userId}/experience`,
        { amount }
      );
      setUsers(prev => prev.map(u => (
        u.id === userId
          ? { ...u, experiencePoints: res.data.experiencePoints, level: res.data.level }
          : u
      )));
      setXpAdjustAmount('');
      setSuccess(t('users.xp_adjust_success'));
    } catch (err) {
      setError(getErrorMessage(err, t('users.xp_adjust_error')));
    } finally {
      setAdjustingXpId(null);
    }
  };

  const handleSave = async (e: FormEvent, userId: number) => {
    if (!validateFormSubmit(e)) return;
    e.preventDefault();
    if (!draft) return;
    if (draft.phoneNumber.trim() && !isValidPhoneNumberInput(draft.phoneNumber)) {
      setError(t('validation.phone_invalid'));
      return;
    }

    setSavingId(userId);
    setError('');
    setSuccess('');
    try {
      await saveUser(userId, draftToPayload(draft));
      setSuccess(t('users.save_success'));
    } catch (err) {
      setError(getErrorMessage(err, t('users.save_error')));
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;

    setDeletingId(deleteTarget.id);
    setError('');
    setSuccess('');
    try {
      await api.delete(`/users/admin/${deleteTarget.id}`, { params: forceDelete ? { force: true } : undefined });
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      if (expandedId === deleteTarget.id) {
        setExpandedId(null);
        setDraft(null);
      }
      setDeleteTarget(null);
      setForceDelete(false);
      setSuccess(t('users.delete_success'));
    } catch (err) {
      setError(getErrorMessage(err, t('users.delete_error')));
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetPassword = async (e: FormEvent, userId: number) => {
    e.preventDefault();
    setPasswordFieldError('');
    if (!validateFormSubmit(e)) return;
    if (newPassword !== confirmPassword) {
      setPasswordFieldError(t('users.password_mismatch'));
      return;
    }
    if (newPassword.length < 6) {
      setPasswordFieldError(t('users.password_too_short'));
      return;
    }

    setPasswordUserId(userId);
    setError('');
    setSuccess('');
    try {
      await setPassword(userId, newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(t('users.password_set_success'));
    } catch (err) {
      setError(getErrorMessage(err, t('users.password_set_error')));
    } finally {
      setPasswordUserId(null);
    }
  };

  const updateDraft = (patch: Partial<AdminUserDraft>) => {
    setDraft(prev => (prev ? { ...prev, ...patch } : prev));
  };

  const colCount = 11;

  if (loading) {
    return <p className="text-muted">{t('users.loading')}</p>;
  }

  return (
    <div>
      <h2 className="section-title">{t('users.title')}</h2>

      {error && (
        <div className="alert alert-error">
          {error}
          <button className="dismiss" onClick={() => setError('')}>&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button className="dismiss" onClick={() => setSuccess('')}>&times;</button>
        </div>
      )}

      <div className="account-tabs" style={{ marginBottom: '1.5rem' }}>
        <button
          type="button"
          className={activeTab === 'whitelist' ? 'active' : ''}
          onClick={() => setActiveTab('whitelist')}
        >
          {t('users.whitelist_title')}
        </button>
        <button
          type="button"
          className={activeTab === 'registered' ? 'active' : ''}
          onClick={() => setActiveTab('registered')}
        >
          {t('users.registered_title')}
        </button>
      </div>

      {activeTab === 'whitelist' && (
        <section className="admin-whitelist-section" style={{ marginBottom: '2rem' }}>
          <h3 className="admin-user-password-title">{t('users.whitelist_title')}</h3>
          <p className="text-muted" style={{ marginBottom: '1rem' }}>{t('users.whitelist_description')}</p>
          <form className="admin-whitelist-form" onSubmit={handleAddWhitelist} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: '1 1 16rem', marginBottom: 0 }}>
              <FormLabel required>Email</FormLabel>
              <input
                type="email"
                value={whitelistEmail}
                onChange={e => setWhitelistEmail(e.target.value)}
                placeholder={t('users.whitelist_email_placeholder')}
                required
                disabled={whitelistSaving}
                aria-required="true"
              />
            </div>
            <label style={{ flex: '1 1 100%', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={whitelistAutoVerify}
                onChange={e => setWhitelistAutoVerify(e.target.checked)}
                disabled={whitelistSaving}
                style={{ marginTop: '0.2rem' }}
              />
              <span>{t('users.whitelist_auto_verify')}</span>
            </label>
            <button type="submit" className="btn btn-primary" disabled={whitelistSaving || !whitelistEmail.trim()}>
              {whitelistSaving && <span className="btn-spinner" aria-hidden="true" />}
              {t('users.whitelist_add')}
            </button>
          </form>
          {whitelistLoading ? (
            <p className="text-muted">{t('users.whitelist_loading')}</p>
          ) : whitelist.length === 0 ? (
            <p className="text-muted">{t('users.whitelist_empty')}</p>
          ) : (
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('users.col_email')}</th>
                    <th>{t('users.whitelist_col_added')}</th>
                    <th>{t('users.whitelist_col_auto_verify')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {whitelist.map(entry => (
                    <tr key={entry.id}>
                      <td>{entry.email}</td>
                      <td>{formatDateOnly(entry.createdAt, lang)}</td>
                      <td>{entry.autoVerify ? '✓' : '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleRemoveWhitelist(entry.id)}
                        >
                          {t('users.whitelist_remove')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {activeTab === 'registered' && (
        <>
          <h3 className="admin-user-password-title">{t('users.registered_title')}</h3>

          {users.length === 0 && !error && (
            <p className="text-muted">{t('users.none')}</p>
          )}

          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-expand-col" aria-label={t('users.col_expand')}></th>
                  <th>{t('users.col_id')}</th>
                  <th>{t('users.col_username')}</th>
                  <th>{t('users.col_name')}</th>
                  <th>{t('users.col_email')}</th>
                  <th>{t('users.col_listings')}</th>
                  <th>{t('users.col_can_offer')}</th>
                  <th>{t('users.col_verified')}</th>
                  <th>{t('users.col_level')}</th>
                  <th>{t('users.col_external_id')}</th>
                  <th>{t('users.col_admin')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const expanded = expandedId === u.id;
                  const current = expanded && draft ? { ...u, ...draft } : u;
                  return (
                    <React.Fragment key={u.id}>
                      <tr className={expanded ? 'admin-table-row-expanded' : undefined}>
                        <td>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm admin-table-expand-btn"
                            onClick={() => toggleExpanded(u)}
                            aria-expanded={expanded}
                            aria-label={expanded ? t('users.collapse') : t('users.expand')}
                          >
                            {expanded ? '▼' : '▶'}
                          </button>
                        </td>
                        <td>{u.id}</td>
                        <td>
                          <Link to={`/user/${u.id}`} className="admin-user-profile-link">
                            {u.username}
                          </Link>
                        </td>
                        <td>{u.firstName} {u.lastName}</td>
                        <td>{u.email}</td>
                        <td>{u.listingCount}</td>
                        <td>
                          {u.canOffer ? (
                            <span className="badge badge-success">{t('users.yes')}</span>
                          ) : (
                            <span
                              className="badge badge-muted"
                              title={translateOfferIssues(u.offerIssues, t).join('; ')}
                            >
                              {t('users.no')}
                            </span>
                          )}
                        </td>
                        <td>
                          {u.isVerified ? (
                            <span className="badge badge-success">{t('users.verified_yes')}</span>
                          ) : (
                            <span className="badge badge-muted">{t('users.verified_no')}</span>
                          )}
                        </td>
                        <td>{u.level} ({u.experiencePoints} XP)</td>
                        <td>{u.hasExternalId ? (u.externalIdProvider || t('users.yes')) : t('users.no')}</td>
                        <td>{u.isAdmin ? t('users.admin_yes') : '—'}</td>
                      </tr>
                      {expanded && draft && (
                        <tr className="admin-user-detail-row">
                          <td colSpan={colCount}>
                            <div className="admin-user-detail">
                              <form className="admin-user-edit-form" onSubmit={e => handleSave(e, u.id)}>
                                <FormLegend />
                                <div className="admin-user-edit-grid">
                              <div className="form-group">
                                <label>{t('users.col_id')}</label>
                                <input type="text" value={u.id} disabled />
                              </div>
                              <div className="form-group">
                                <FormLabel required>{t('users.col_username')}</FormLabel>
                                <input
                                  type="text"
                                  value={draft.username}
                                  onChange={e => updateDraft({ username: e.target.value })}
                                  required
                                  disabled={savingId === u.id && saving}
                                  aria-required="true"
                                />
                              </div>
                              <div className="form-group">
                                <FormLabel required>{t('account.email')}</FormLabel>
                                <input
                                  type="email"
                                  value={draft.email}
                                  onChange={e => updateDraft({ email: e.target.value })}
                                  required
                                  disabled={savingId === u.id && saving}
                                  aria-required="true"
                                />
                              </div>
                              <div className="form-group">
                                <FormLabel required>{t('account.first_name')}</FormLabel>
                                <input
                                  type="text"
                                  value={draft.firstName}
                                  onChange={e => updateDraft({ firstName: e.target.value })}
                                  required
                                  disabled={savingId === u.id && saving}
                                  aria-required="true"
                                />
                              </div>
                              <div className="form-group">
                                <FormLabel required>{t('account.last_name')}</FormLabel>
                                <input
                                  type="text"
                                  value={draft.lastName}
                                  onChange={e => updateDraft({ lastName: e.target.value })}
                                  required
                                  disabled={savingId === u.id && saving}
                                  aria-required="true"
                                />
                              </div>
                              <div className="form-group">
                                <label>{t('account.date_of_birth')}</label>
                                <DateOfBirthInput
                                  value={draft.dateOfBirth}
                                  onChange={v => updateDraft({ dateOfBirth: v })}
                                  disabled={savingId === u.id && saving}
                                />
                              </div>
                              <div className="form-group">
                                <label>{t('account.settings_phone_number')}</label>
                                <PhoneInput
                                  value={draft.phoneNumber}
                                  onChange={v => updateDraft({ phoneNumber: v })}
                                  placeholder={t('login.phone_placeholder')}
                                  disabled={savingId === u.id && saving}
                                  invalidMessage={t('validation.phone_invalid')}
                                />
                              </div>
                              <div className="form-group">
                                <label>{t('account.precise_address_street')}</label>
                                <input
                                  type="text"
                                  value={draft.street}
                                  onChange={e => updateDraft({ street: e.target.value })}
                                  disabled={savingId === u.id && saving}
                                />
                              </div>
                              <div className="form-group">
                                <label>{t('account.precise_address_house_number')}</label>
                                <input
                                  type="text"
                                  value={draft.houseNumber}
                                  onChange={e => updateDraft({ houseNumber: e.target.value })}
                                  disabled={savingId === u.id && saving}
                                />
                              </div>
                              <div className="form-group">
                                <label>{t('account.precise_address_city')}</label>
                                <input
                                  type="text"
                                  value={draft.city}
                                  onChange={e => updateDraft({ city: e.target.value })}
                                  disabled={savingId === u.id && saving}
                                />
                              </div>
                              <div className="form-group">
                                <label>{t('account.precise_address_country')}</label>
                                <select
                                  value={draft.country}
                                  onChange={e => updateDraft({ country: e.target.value })}
                                  disabled={savingId === u.id && saving}
                                >
                                  {COUNTRY_CODES.map(code => (
                                    <option key={code} value={code}>{t(countryLabelKey(code))}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group admin-user-checkbox-field">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={draft.isVerified}
                                    onChange={e => updateDraft({ isVerified: e.target.checked })}
                                    disabled={savingId === u.id && saving}
                                  />
                                  {' '}{t('users.col_verified')}
                                </label>
                              </div>
                              <div className="form-group admin-user-checkbox-field">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={draft.isAdmin}
                                    onChange={e => updateDraft({ isAdmin: e.target.checked })}
                                    disabled={savingId === u.id && saving}
                                  />
                                  {' '}{t('users.col_admin')}
                                </label>
                              </div>
                            </div>

                            <div className="admin-user-readonly-section">
                              <ReadOnlyField
                                label={t('users.view_public_profile')}
                                value={
                                  <Link to={`/user/${u.id}`} target="_blank" rel="noopener noreferrer">
                                    {t('users.view_public_profile_link')}
                                  </Link>
                                }
                              />
                              <ReadOnlyField
                                label={t('users.field_external_id_provider')}
                                value={displayValue(current.externalIdProvider)}
                              />
                              <ReadOnlyField
                                label={t('users.field_external_id_sub')}
                                value={displayValue(current.externalIdSub)}
                              />
                              <ReadOnlyField
                                label={t('users.field_verified_at')}
                                value={current.verifiedAt ? formatDateOnly(current.verifiedAt, lang) : '—'}
                              />
                              <ReadOnlyField
                                label={t('users.field_verification_method')}
                                value={current.verificationMethod ? t(`users.verification_method_${current.verificationMethod}`) : '—'}
                              />
                              <ReadOnlyField
                                label={t('users.field_verified_by')}
                                value={displayValue(current.verifiedByUsername)}
                              />
                              <ReadOnlyField
                                label={t('account.location_precise_coords')}
                                value={
                                  current.preciseLatitude != null && current.preciseLongitude != null
                                    ? `${current.preciseLatitude.toFixed(5)}, ${current.preciseLongitude.toFixed(5)}`
                                    : '—'
                                }
                              />
                              <ReadOnlyField
                                label={t('account.location_approximate_coords')}
                                value={
                                  current.approximateLatitude != null && current.approximateLongitude != null
                                    ? `${current.approximateLatitude.toFixed(5)}, ${current.approximateLongitude.toFixed(5)}`
                                    : '—'
                                }
                              />
                            </div>

                            <div className="form-actions">
                              <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={savingId === u.id && saving}
                              >
                                {savingId === u.id && saving && <span className="btn-spinner" aria-hidden="true" />}
                                {t('users.save')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancel}
                                disabled={savingId === u.id && saving}
                              >
                                {t('users.cancel')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => { setDeleteTarget(u); setForceDelete(false); }}
                                disabled={u.id === currentUserId || deletingId === u.id}
                                title={u.id === currentUserId ? t('users.delete_self_disabled') : undefined}
                              >
                                {t('users.delete')}
                              </button>
                            </div>
                          </form>

                          <form
                            className="admin-user-password-form"
                            onSubmit={e => handleAdjustXp(e, u.id)}
                          >
                            <h3 className="admin-user-password-title">{t('users.xp_section')}</h3>
                            <div className="admin-user-edit-grid">
                              <ReadOnlyField
                                label={t('users.col_level')}
                                value={`${u.level} (${u.experiencePoints} XP)`}
                              />
                              <div className="form-group">
                                <label>{t('users.xp_adjust_amount')}</label>
                                <input
                                  type="number"
                                  value={xpAdjustAmount}
                                  onChange={e => setXpAdjustAmount(e.target.value)}
                                  placeholder={t('users.xp_adjust_placeholder')}
                                  disabled={adjustingXpId === u.id}
                                />
                              </div>
                            </div>
                            <div className="form-actions">
                              <button
                                type="submit"
                                className="btn btn-secondary"
                                disabled={!xpAdjustAmount || adjustingXpId === u.id}
                              >
                                {adjustingXpId === u.id && (
                                  <span className="btn-spinner" aria-hidden="true" />
                                )}
                                {t('users.xp_adjust_submit')}
                              </button>
                            </div>
                          </form>

                          <form
                            className="admin-user-password-form"
                            onSubmit={e => handleSetPassword(e, u.id)}
                          >
                            <h3 className="admin-user-password-title">{t('users.password_section')}</h3>
                            <div className="admin-user-edit-grid">
                              <div className={`form-group${passwordFieldError ? ' form-group--error' : ''}`}>
                                <FormLabel required>{t('users.set_password')}</FormLabel>
                                <input
                                  type="password"
                                  value={newPassword}
                                  onChange={e => { setNewPassword(e.target.value); setPasswordFieldError(''); }}
                                  autoComplete="new-password"
                                  required
                                  disabled={passwordUserId === u.id && settingPassword}
                                  aria-required="true"
                                  aria-invalid={passwordFieldError ? true : undefined}
                                  aria-describedby={passwordFieldError ? 'admin-password-error' : undefined}
                                />
                              </div>
                              <div className={`form-group${passwordFieldError ? ' form-group--error' : ''}`}>
                                <FormLabel required>{t('users.password_confirm')}</FormLabel>
                                <input
                                  type="password"
                                  value={confirmPassword}
                                  onChange={e => { setConfirmPassword(e.target.value); setPasswordFieldError(''); }}
                                  autoComplete="new-password"
                                  required
                                  disabled={passwordUserId === u.id && settingPassword}
                                  aria-required="true"
                                  aria-invalid={passwordFieldError ? true : undefined}
                                />
                              </div>
                            </div>
                            <FieldError id="admin-password-error" message={passwordFieldError} />
                            <div className="form-actions">
                              <button
                                type="submit"
                                className="btn btn-secondary"
                                disabled={passwordUserId === u.id && settingPassword}
                              >
                                {passwordUserId === u.id && settingPassword && (
                                  <span className="btn-spinner" aria-hidden="true" />
                                )}
                                {t('users.set_password')}
                              </button>
                            </div>
                          </form>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
                </tbody>
              </table>
            </div>
          </>
        )}

      <Modal
        open={deleteTarget != null}
        onClose={() => {
          if (!deletingId) {
            setDeleteTarget(null);
            setForceDelete(false);
          }
        }}
        title={t('users.delete_title')}
        closeOnOverlayClick={!deletingId}
        closeOnEscape={!deletingId}
      >
        {deleteTarget && (
          <>
            <p>{t('users.delete_confirm', { username: deleteTarget.username })}</p>
            <p className="text-muted">
              {forceDelete ? t('users.delete_force_warning') : t('users.delete_warning')}
            </p>
            <div className="form-group admin-user-checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={forceDelete}
                  onChange={e => setForceDelete(e.target.checked)}
                  disabled={deletingId != null}
                />
                {' '}{t('users.delete_force_label')}
              </label>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setDeleteTarget(null); setForceDelete(false); }}
                disabled={deletingId != null}
              >
                {t('users.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteUser}
                disabled={deletingId != null}
              >
                {deletingId != null && <span className="btn-spinner" aria-hidden="true" />}
                {t('users.delete')}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};
